import type { ClassData, LibraryData } from '@/core/types/library.type'
import { analyzeMethodGraftability, analyzeTypeUsageGraftability, buildHostedTypeFqns } from '@/core/utils/graftability'

/** typeFqn -> (memberName -> graftable). Member names include the runtime constructor token. */
export type MethodGraftabilityIndex = Map<string, Map<string, boolean>>

function classFqn(classData: ClassData): string {
  return `${classData.namespace}.${classData.name}`
}

/**
 * Build a per-type, per-member graftability lookup so the wizard can flag non-graftable
 * methods and auto-select only the graftable ones. Overloads sharing a name are ANDed
 * (a name is graftable only when every overload is graftable).
 */
export function buildMethodGraftabilityIndex(
  libraryData: LibraryData,
  constructorToken: string
): MethodGraftabilityIndex {
  const hostedTypeFqns = buildHostedTypeFqns(libraryData)
  const index: MethodGraftabilityIndex = new Map()

  for (const classData of Object.values(libraryData.classes ?? {})) {
    const memberMap = new Map<string, boolean>()

    for (const method of Object.values(classData.methods ?? {})) {
      const graftable = analyzeMethodGraftability(method, classData, hostedTypeFqns).length === 0
      memberMap.set(method.name, (memberMap.get(method.name) ?? true) && graftable)
    }

    const constructors = Object.values(classData.constructors ?? {})
    if (constructors.length > 0) {
      const graftable = constructors.every((constructor) =>
        constructor.parameters.every(
          (parameter) => analyzeTypeUsageGraftability(parameter.typeUsage, { hostedTypeFqns }).graftable
        )
      )
      memberMap.set(constructorToken, graftable)
    }

    index.set(classFqn(classData), memberMap)
  }

  return index
}
